import { StyledSelect } from "@ando/ui";
/**
 * CreateTemplateDialog — single-shot dialog for the "Nuovo template" CTA.
 *
 * The dialog collects ``name`` + ``description`` + optional inheritance
 * source, calls ``createTemplate`` via the mutation hook, and forwards the
 * created slug to the parent so the editor can navigate to the new
 * detail view. We use the existing ``<Dialog>`` primitive from ``@ando/ui``
 * so chrome + animation tokens match the rest of the app.
 */

import * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ando/ui";
import type { TemplateKind, TemplateSummary } from "@ando/core";

import { useCreateTemplate } from "../hooks/useTemplateMutations";
import { DEFAULT_TEMPLATE_SPECS } from "./TemplateFieldSpecs";

export interface CreateTemplateDialogProps {
  readonly kind: TemplateKind;
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  /** Available templates the user can inherit the spec from. */
  readonly inheritSources: readonly TemplateSummary[];
  /**
   * Resolver that pulls the full spec of an inheritance source. We pass
   * this in so the dialog stays detached from query cache concerns — the
   * parent (which already loaded the detail when needed) provides the
   * resolved spec via a quick local lookup.
   */
  readonly resolveInheritSpec: (identifier: string) => Promise<Record<string, unknown> | null>;
  readonly onCreated: (slug: string) => void;
}

const inputCls =
  "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ring";

const KIND_LABEL: Record<TemplateKind, ReturnType<typeof msg>> = {
  chrome: msg`Chrome · slide deck`,
  paper: msg`Paper · documento`,
  writing_style: msg`Voice · stile di scrittura`,
};

export const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  kind,
  open,
  onOpenChange,
  inheritSources,
  resolveInheritSpec,
  onCreated,
}) => {
  const { t, i18n } = useLingui();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [inheritFrom, setInheritFrom] = React.useState<string>("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const createTpl = useCreateTemplate();

  // Reset form whenever the dialog opens fresh so prior state doesn't leak
  // across kinds (Chrome → Paper → Voice).
  React.useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setInheritFrom("");
      setSubmitError(null);
    }
  }, [open]);

  const canSubmit =
    name.trim().length > 0 &&
    description.trim().length >= 10 &&
    !createTpl.isPending;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);

    // Resolve the inheritance spec (or fall back to the kind's default
    // — the BE validator requires a non-empty spec).
    let inheritedSpec: Record<string, unknown> | null = null;
    if (inheritFrom) {
      inheritedSpec = await resolveInheritSpec(inheritFrom);
    }
    const spec = inheritedSpec ?? DEFAULT_TEMPLATE_SPECS[kind];

    try {
      const created = await createTpl.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        kind,
        spec,
      });
      if (created.slug) {
        onCreated(created.slug);
        onOpenChange(false);
      } else {
        // BE always returns a slug on POST 201 — defensive guard.
        setSubmitError(t`Risposta inattesa dal server: slug mancante.`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Nuovo template</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Crea un template {i18n._(KIND_LABEL[kind])} personalizzato. La
              descrizione deve contenere almeno 10 caratteri.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-medium text-ink-2 text-xs">
              <Trans>Nome</Trans> <span className="text-accent">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={64}
              placeholder={t`Es. ACME · Pitch deck`}
              className={inputCls}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium text-ink-2 text-xs">
              <Trans>Descrizione</Trans> <span className="text-accent">*</span>
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              minLength={10}
              maxLength={500}
              rows={3}
              placeholder={t`A cosa serve questo template? Quando va usato?`}
              className={inputCls}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium text-ink-2 text-xs">
              <Trans>Eredita da (opzionale)</Trans>
            </span>
            <StyledSelect
              value={inheritFrom}
              onChange={(e) => setInheritFrom(e.target.value)}
              className={inputCls}
            >
              <option value="">{t`— Inizia da capo (default) —`}</option>
              {inheritSources.map((t) => {
                const id = t.slug ?? t.name;
                return (
                  <option key={`${t.kind}:${id}`} value={id}>
                    {t.name}
                  </option>
                );
              })}
            </StyledSelect>
          </label>

          {submitError ? (
            <p className="text-[12px] text-warning">{submitError}</p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={createTpl.isPending}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createTpl.isPending ? t`Creazione…` : t`Crea template`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
CreateTemplateDialog.displayName = "CreateTemplateDialog";
